import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Icon } from '../../../base-components';
import FilesList from '../../../../components/FilesList';
import { IServerJobApplicationEmailFile } from '../../../../common';

interface EditorAttachmentsProps {
    wrapperRef: React.RefObject<HTMLDivElement>;
    attachments?: File[];
    onFilesAttached: (files: File[]) => void;
    onRemoveAttachment?: (index: number) => void;
}

const EditorAttachments = ({
    wrapperRef,
    attachments,
    onFilesAttached,
    onRemoveAttachment,
}: EditorAttachmentsProps) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [toolbarPortal, setToolbarPortal] = useState<HTMLElement | null>(
        null
    );
    const [containerPortal, setContainerPortal] = useState<HTMLElement | null>(
        null
    );

    useEffect(() => {
        if (!wrapperRef.current) return;

        const toolbar = wrapperRef.current.querySelector('.ql-toolbar');
        if (!toolbar) return;

        const span = document.createElement('span');
        span.className = 'ql-formats';
        toolbar.appendChild(span);
        setToolbarPortal(span);

        const container = wrapperRef.current.querySelector('.ql-container');
        if (container) {
            const filesDiv = document.createElement('div');
            container.appendChild(filesDiv);
            setContainerPortal(filesDiv);
        }

        return () => {
            span.remove();
            setToolbarPortal(null);
            setContainerPortal(null);
        };
    }, [wrapperRef]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            onFilesAttached(Array.from(files));
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const filesForList: IServerJobApplicationEmailFile[] = (
        attachments || []
    ).map((file, index) => ({
        id: index,
        file_name: file.name,
        file_url: '',
        size: Number((file.size / (1024 * 1024)).toFixed(1)),
    }));

    return (
        <>
            {toolbarPortal &&
                createPortal(
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        style={{
                            outline: 'none',
                        }}
                    >
                        <Icon
                            name="AttachFileIcon"
                            size={18}
                            style={{ transform: 'rotate(45deg)' }}
                        />
                    </button>,
                    toolbarPortal
                )}
            <input
                type="file"
                multiple
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }}
            />
            {filesForList.length > 0 &&
                onRemoveAttachment &&
                containerPortal &&
                createPortal(
                    <Box sx={{ p: 2, pt: 0 }}>
                        <FilesList
                            files={filesForList}
                            onRemove={onRemoveAttachment}
                        />
                    </Box>,
                    containerPortal
                )}
        </>
    );
};

export default EditorAttachments;
